import React, { useState, useRef, useEffect } from 'react';
import FaiBox from "../../../../../MuiComponents/FaiBox";
import { Card, List, ListItem, Box, Typography, IconButton } from "@mui/material";
import FaiTypography from "../../../../../MuiComponents/FaiTypography";
import theme from "../../../../../assets/theme";
import FaiButton from "../../../../../MuiComponents/FaiButton";
import FaiSelect from '../../../../../MuiComponents/FaiSelect';
import { toast } from 'react-toastify';
import apiService from '../../../../../api/apiService';
import ProductController from '../../../../../controllers/productController';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ProductType } from '../../../../../types/productTypes';

interface ApiResponse {
    status: number;
    message: string;
    data: any;
}

interface CategoryOption {
    value: string;
    label: string;
}

function MultiProduct() {
    const { typography, palette, borders, boxShadows } = theme;
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryOption | null>(null);
    const [selectedProductType, setSelectedProductType] = useState<CategoryOption | null>(null);
    const [productTypes, setProductTypes] = useState<ProductType[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [masterCategories, setMasterCategories] = useState<any[]>([]);
    const [loading, setLoading] = useState({
        categories: false,
        productTypes: false,
        productTypeMetadata: false,
        downloading: false
    });

    const fetchMasterCategories = async () => {
        setLoading(prev => ({ ...prev, categories: true }));
        try {
            const response = await ProductController.getMasterCategories();
            if (response.data?.data?.masterCategories) {
                setMasterCategories(response.data.data.masterCategories);
            }
        } catch (error) {
            console.error('Error fetching master categories:', error);
        } finally {
            setLoading(prev => ({ ...prev, categories: false }));
        }
    };

    const fetchProductTypes = async (categoryId: string) => {
        setLoading(prev => ({ ...prev, productTypes: true }));
        try {
            const response = await ProductController.getCategoryByMastercategoryId(categoryId);
            if (response?.data?.status === 200 && response?.data?.data?.categories) {
                setProductTypes(response.data.data.categories);
            }
        } catch (error) {
            console.error('Error fetching product types:', error);
            toast.error('Failed to fetch product types');
        } finally {
            setLoading(prev => ({ ...prev, productTypes: false }));
        }
    };

    useEffect(() => {
        fetchMasterCategories();

        return () => {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };
    }, []);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Check file extension
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (fileExtension === 'csv' || fileExtension === 'xls' || fileExtension === 'xlsx') {
                setUploadedFile(file);
                console.log('File selected:', file.name);
            } else {
                toast.error('Please upload a valid CSV or Excel file');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const handleFileRemove = () => {
        setUploadedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUpload = async () => {
        if (!uploadedFile) {
            toast.error('Please select a file to upload');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('excelFile', uploadedFile);
            

            const response = await apiService.post<ApiResponse>(
                '/products/upload-bulk-products-excel',
                formData
            );

            if (response?.data?.status === 201) {
                toast.success(response.data.message);
                handleFileRemove();
            } else {
                throw new Error(response?.data?.message || 'Upload failed');
            }
        } catch (error: any) {
            console.error('Error uploading products:', error);
            toast.error(error?.response?.data?.message || 'Failed to upload products. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleCategoryChange = (value: CategoryOption) => {
        setSelectedCategory(value);
        setSelectedProductType(null);
        if (value) {
            fetchProductTypes(value.value);
        }
    };

    const handleProductTypeChange = (value: CategoryOption) => {
        setSelectedProductType(value);
    };

    const handleDownloadTemplate = async () => {
        if (!selectedProductType) {
            toast.error('Please select a product type first');
            return;
        }

        setLoading(prev => ({ ...prev, downloading: true }));
        try {
            const response = await ProductController.getDownloadTemplateExcel(selectedProductType.value);
            console.log(response.data.data.templateExcelMedia, '')
            const fileUrl = response.data.data.templateExcelMedia
            if (fileUrl) {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', 'template.xlsx'); // You can set any filename here
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success('Template downloaded successfully');
            } }
            catch (error: any) {
                console.error('Error downloading template:', error);
                toast.error(error?.response?.data?.message || 'Failed to download template. Please try again.');
            } finally {
                setLoading(prev => ({ ...prev, downloading: false }));
            }
        };

        return (
            <FaiBox>
                <FaiBox bgColor={palette.white.main} borderRadius={borders.borderRadius.md} mb={2} boxShadow={boxShadows.xs}>
                    <FaiBox p={"40px 16px"}>
                        <FaiBox display={"flex"} justifyContent={"center"} alignItems={"center"} mb={5}>
                            <FaiTypography variant={"span"} fontSize={typography.size.md} sx={{ fontWeight: 500 }}>
                                To add multiple products at once, download the editable Template file and fill the required details as mentioned.
                            </FaiTypography>
                        </FaiBox>

                        <FaiBox mb={3}>
                            <FaiBox mb={1} ml={0.5} display="inline-block">
                                <FaiTypography component="label" variant="caption" fontWeight="bold" textTransform="capitalize">
                                    Choose Category
                                </FaiTypography>
                            </FaiBox>
                            <FaiSelect
                                placeholder={"Choose Category"}
                                name={"masterCategoryId"}
                                options={masterCategories.map(category => ({
                                    value: category.id,
                                    label: category.name
                                }))}
                                onChange={handleCategoryChange}
                                value={selectedCategory}
                                isLoading={loading.categories}
                            />
                        </FaiBox>

                        <FaiBox mb={3}>
                            <FaiBox mb={1} ml={0.5} display="inline-block">
                                <FaiTypography component="label" variant="caption" fontWeight="bold" textTransform="capitalize">
                                    Choose Sub Category
                                </FaiTypography>
                            </FaiBox>
                            <FaiSelect
                                placeholder={"Choose Sub Category"}
                                name={"categoryId"}
                                options={productTypes.map(type => ({
                                    value: type.id,
                                    label: type.name
                                }))}
                                onChange={handleProductTypeChange}
                                value={selectedProductType}
                                isLoading={loading.productTypes}
                                isDisabled={!selectedCategory}
                            />
                        </FaiBox>

                        <FaiBox display={"flex"} justifyContent={"center"} alignItems={"center"}>
                            <FaiButton
                                variant={"outlined"}
                                color={"success"}
                                onClick={handleDownloadTemplate}
                                disabled={!selectedProductType || loading.downloading}
                            >
                                {loading.downloading ? 'Downloading...' : 'Download Template'}
                            </FaiButton>
                        </FaiBox>
                    </FaiBox>
                </FaiBox>

                <FaiBox display={"flex"} justifyContent={"center"} alignItems={"center"} mb={4}>
                    <FaiBox bgColor={palette.white.main} borderRadius={borders.borderRadius.md} boxShadow={boxShadows.xs}>
                        <FaiBox p={"24px 40px"} sx={{ width: "530px" }}>
                            <FaiBox mb={5} textAlign={"center"}>
                                <FaiTypography variant={"span"} fontSize={typography.size.md} sx={{ fontWeight: 500 }}>
                                    Select the edited CSV file
                                </FaiTypography>
                            </FaiBox>

                            <FaiBox mb={2}>
                                <Box
                                    sx={{
                                        border: `2px dashed ${palette.grey[300]}`,
                                        borderRadius: borders.borderRadius.md,
                                        p: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        minHeight: '150px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        '&:hover': {
                                            borderColor: palette.primary.main,
                                        },
                                    }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        accept=".csv,.xls,.xlsx"
                                        style={{ display: 'none' }}
                                    />
                                    {!uploadedFile ? (
                                        <>
                                            <CloudUploadIcon sx={{ fontSize: 48, color: palette.grey[400], mb: 2 }} />
                                            <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                                                Drop files here or click to upload
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Supported formats: CSV, XLS, XLSX
                                            </Typography>
                                        </>
                                    ) : (
                                        <Box sx={{
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 2
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                width: '100%',
                                                justifyContent: 'center'
                                            }}>
                                                <CheckCircleIcon sx={{ color: palette.success.main }} />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        wordBreak: 'break-all',
                                                        textAlign: 'center',
                                                        maxWidth: '80%'
                                                    }}
                                                >
                                                    {uploadedFile.name}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileRemove();
                                                }}
                                                sx={{ mt: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            </FaiBox>

                            <FaiBox mb={4}>
                                <FaiTypography variant={"caption"} lineHeight={2}>
                                    <List sx={{ listStyleType: 'disc', pl: 2 }}>
                                        <ListItem sx={{ display: 'list-item' }}>
                                            Ensure all the fields are checked and corrected.
                                        </ListItem>
                                        <ListItem sx={{ display: 'list-item' }}>
                                            Do not re-upload styles and articles that are already submitted and still being processed.
                                        </ListItem>
                                    </List>
                                </FaiTypography>
                            </FaiBox>

                            <FaiBox>
                                <FaiButton
                                    color="primary"
                                    variant={"contained"}
                                    fullWidth
                                    onClick={handleUpload}
                                    disabled={!uploadedFile || uploading }
                                    sx={{ mt: 2 }}
                                >
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </FaiButton>
                            </FaiBox>
                        </FaiBox>
                    </FaiBox>
                </FaiBox>
            </FaiBox>
        );
    }

    export default MultiProduct;
